import React, { useEffect, useMemo, useState } from 'react';
import { patchCompany } from '../../../../api/company/company';
import { useAuth } from '../../../../context/AuthContext';
import { useNotification } from '../../../../context/NotificationContext';
import { useTutorial } from '../../../tutorial/TutorialContext';
import { buildSharedSlugUpdate, getCompanySlug } from '../../../../client-experience/utils/companyAccess';
import {
  formatPortalSlugCooldownMessage,
  getActivePortalSlugCooldownStatus
} from '../../../../client-experience/utils/portalSlugCooldown';
import {
  ensurePortalSlugCanBeSaved,
  isReservedPortalSegment,
  previewPortalSlugAvailability,
  validatePortalUrl
} from '../../../../client-experience/utils/companyUtils';
import ConfirmDialog from '../../../../shared/components/confirm-dialog/ConfirmDialog';
import LandingPageSettings from '../LandingPageSettings';
import './styles/ClientExperience.css';

const ClientExperienceSettings = ({ onUnsavedChangesChange }) => {
  const { companyData, updateCompanyData } = useAuth();
  const { markStepComplete } = useTutorial();
  const [portalSettings, setPortalSettings] = useState({
    customUrl: getCompanySlug(companyData)
  });
  const [slugFieldError, setSlugFieldError] = useState(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [pendingSaveSlug, setPendingSaveSlug] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const { setNotification } = useNotification();
  const savedSlug = getCompanySlug(companyData);
  const sharedSlug = validatePortalUrl(portalSettings.customUrl || '');
  const websiteUrl = sharedSlug ? `${window.location.origin}/book/${sharedSlug}` : '';

  const slugCooldownStatus = useMemo(
    () => getActivePortalSlugCooldownStatus(companyData, savedSlug),
    [companyData, savedSlug]
  );

  const isSlugInputLocked = slugCooldownStatus.allowed === false;

  useEffect(() => {
    setPortalSettings({
      customUrl: getCompanySlug(companyData)
    });
    setSlugFieldError(null);
    setSlugChecking(false);
    setConfirmSaveOpen(false);
    setPendingSaveSlug('');
  }, [companyData]);

  useEffect(() => {
    if (!companyData?.id || isSlugInputLocked) {
      return undefined;
    }

    const normalizedSlug = validatePortalUrl(portalSettings.customUrl || '');

    if (!normalizedSlug || normalizedSlug === savedSlug) {
      setSlugFieldError(null);
      setSlugChecking(false);
      return undefined;
    }

    if (isReservedPortalSegment(normalizedSlug)) {
      setSlugFieldError('This website name is reserved. Please choose a different one.');
      setSlugChecking(false);
      return undefined;
    }

    if (!slugCooldownStatus.allowed) {
      setSlugFieldError(null);
      setSlugChecking(false);
      return undefined;
    }

    setSlugChecking(true);
    const timeoutId = window.setTimeout(async () => {
      const result = await previewPortalSlugAvailability(
        portalSettings.customUrl,
        companyData.id,
        savedSlug
      );
      setSlugFieldError(result.ok ? null : result.message);
      setSlugChecking(false);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [
    portalSettings.customUrl,
    companyData?.id,
    savedSlug,
    isSlugInputLocked,
    slugCooldownStatus
  ]);

  const handleWebsiteUnsavedChangesChange = (hasUnsavedChanges) => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleCopyUrl = async (value, successMessage) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      showNotification(successMessage, 'success');
    } catch (error) {
      console.error('Copy failed:', error);
      showNotification('Failed to copy URL', 'error');
    }
  };

  const handleSaveAccessSettings = async (nextPortalSettings = portalSettings, successMessage = 'Client experience updated') => {
    try {
      if (!companyData?.id) {
        throw new Error('No company data available');
      }

      const slugValidation = await ensurePortalSlugCanBeSaved(nextPortalSettings.customUrl || '', companyData.id, {
        companyData,
        savedSlug
      });
      if (!slugValidation.ok) {
        showNotification(slugValidation.error, 'error');
        return false;
      }

      const validatedUrl = slugValidation.normalizedSlug;
      const isSlugChange = validatedUrl !== savedSlug;

      const updatedSettings = {
        ...nextPortalSettings,
        customUrl: validatedUrl
      };
      const sharedSlugUpdate = buildSharedSlugUpdate(companyData, validatedUrl);
      const updatedPortalSettings = {
        ...sharedSlugUpdate.portalSettings,
        ...updatedSettings,
        isEnabled: true,
        allowRegistration: true,
        ...(isSlugChange ? { customUrlChangedAt: new Date() } : {})
      };
      const updatedLandingPageSettings = sharedSlugUpdate.landingPageSettings;

      const company = await patchCompany({
        companyId: companyData.id,
        company: {
          portalSettings: updatedPortalSettings,
          landingPageSettings: updatedLandingPageSettings,
        },
      });

      updateCompanyData({
        ...companyData,
        ...company,
        portalSettings: updatedPortalSettings,
        landingPageSettings: updatedLandingPageSettings
      });

      setPortalSettings(updatedSettings);
      setSlugFieldError(null);
      showNotification(successMessage, 'success');
      markStepComplete(7);
      return true;
    } catch (error) {
      console.error('Error saving client experience settings:', error);
      showNotification('Failed to update client experience', 'error');
      return false;
    }
  };

  const handleResetWebsiteName = () => {
    setPortalSettings({ customUrl: savedSlug });
    setSlugFieldError(null);
    setSlugChecking(false);
    setPendingSaveSlug('');
  };

  const handleSharedUrlBlur = async () => {
    if (confirmSaveOpen || savingSlug) {
      return;
    }

    const validatedUrl = validatePortalUrl(portalSettings.customUrl || '');

    if (!validatedUrl || validatedUrl === savedSlug) {
      handleResetWebsiteName();
      return;
    }

    if (isSlugInputLocked) {
      handleResetWebsiteName();
      return;
    }

    const slugValidation = await ensurePortalSlugCanBeSaved(validatedUrl, companyData.id, {
      companyData,
      savedSlug
    });

    if (!slugValidation.ok) {
      setSlugFieldError(slugValidation.error);
      return;
    }

    setPendingSaveSlug(slugValidation.normalizedSlug);
    setConfirmSaveOpen(true);
  };

  const handleConfirmSaveWebsiteName = async () => {
    if (!pendingSaveSlug) {
      setConfirmSaveOpen(false);
      return;
    }

    setSavingSlug(true);
    try {
      const saved = await handleSaveAccessSettings(
        { ...portalSettings, customUrl: pendingSaveSlug },
        savedSlug ? 'Website name updated' : 'Website name saved'
      );
      if (saved) {
        setConfirmSaveOpen(false);
        setPendingSaveSlug('');
      }
    } finally {
      setSavingSlug(false);
    }
  };

  const handleCancelSaveWebsiteName = () => {
    if (savingSlug) {
      return;
    }
    setConfirmSaveOpen(false);
    setPendingSaveSlug('');
    handleResetWebsiteName();
  };

  const confirmSlug = pendingSaveSlug || sharedSlug;
  const confirmWebsiteUrl = confirmSlug ? `${window.location.origin}/book/${confirmSlug}` : '';
  const confirmDialogMessage = savedSlug
    ? (
      <>
        Change your website name to <strong>{confirmSlug}</strong>?
        {' '}Your new booking link will be{' '}
        <strong>{confirmWebsiteUrl}</strong>.
        {' '}You will not be able to change it again for 30 days.
      </>
    )
    : (
      <>
        Use <strong>{confirmSlug}</strong> as your website name?
        {' '}Your booking link will be{' '}
        <strong>{confirmWebsiteUrl}</strong>.
        {' '}After saving, you can only change it once every 30 days.
      </>
    );

  return (
    <div className="ce-shell">
      <section className="ce-card ce-card--hero">
        <div className="ce-shell__hero">
          <div>
            <h2 className="ce-title">Client Experience</h2>
            <p className="ce-description">
              Choose your own website name and booking link. This is separate from your company name and is not set until you save it here.
            </p>
          </div>
        </div>

        <div className="ce-subcard ce-muted-card ce-access-url">
          <div className="ce-access-url__grid">
            <div className="ce-form-item">
              <label className="ce-label" htmlFor="client-experience-website-name">Website name</label>
              <div className="ce-access-url__input-stack">
                <input
                  id="client-experience-website-name"
                  className={`ce-field${slugFieldError ? ' ce-field--error' : ''}`}
                  type="text"
                  placeholder="your-business"
                  value={portalSettings.customUrl}
                  onChange={(e) => setPortalSettings((currentSettings) => ({
                    ...currentSettings,
                    customUrl: validatePortalUrl(e.target.value)
                  }))}
                  onBlur={handleSharedUrlBlur}
                  autoComplete="off"
                  readOnly={isSlugInputLocked}
                  aria-invalid={slugFieldError ? 'true' : 'false'}
                  aria-describedby={
                    slugFieldError
                      ? 'client-experience-website-name-error'
                      : (isSlugInputLocked ? 'client-experience-website-name-cooldown' : undefined)
                  }
                />
                <div className="ce-field-feedback" aria-live="polite">
                  {!isSlugInputLocked && slugChecking && !slugFieldError && (
                    <p className="ce-field-message ce-field-message--checking">Checking availability...</p>
                  )}
                  {!isSlugInputLocked && slugFieldError && (
                    <p
                      id="client-experience-website-name-error"
                      className="ce-field-message ce-field-message--error"
                      role="alert"
                    >
                      {slugFieldError}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="ce-form-item">
              <label className="ce-label" htmlFor="client-experience-full-url">Website link</label>
              <div className="ce-copy-field">
                <input
                  id="client-experience-full-url"
                  className="ce-readonly-field"
                  readOnly
                  value={websiteUrl || `${window.location.origin}/book/your-business`}
                />
                <button
                  type="button"
                  className="ce-button ce-button--secondary ce-access-url__copy"
                  onClick={() => handleCopyUrl(websiteUrl, 'Booking link copied to clipboard')}
                  disabled={!websiteUrl}
                >
                  Copy URL
                </button>
              </div>
            </div>
          </div>

          {isSlugInputLocked && (
            <p
              id="client-experience-website-name-cooldown"
              className="ce-field-message ce-field-message--cooldown"
            >
              {formatPortalSlugCooldownMessage(slugCooldownStatus)}
            </p>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={confirmSaveOpen}
        title={savedSlug ? 'Change website name?' : 'Use this website name?'}
        message={confirmDialogMessage}
        confirmText={savedSlug ? 'Yes, change it' : 'Yes, use this name'}
        cancelText="No, go back"
        busy={savingSlug}
        onCancel={handleCancelSaveWebsiteName}
        onConfirm={handleConfirmSaveWebsiteName}
      />

      <section className="ce-card ce-card--panel">
        <div className="ce-workspace-switcher">
          <div className="ce-workspace-switcher__header">
            <div>
              <h2 className="ce-section-title">Website Builder</h2>
              <p className="ce-section-description">
                Customize what new visitors see before they book.
              </p>
            </div>
          </div>
        </div>

        <div
          className="ce-panel-body ce-panel-body--workspace"
        >
          <LandingPageSettings embedded onUnsavedChangesChange={handleWebsiteUnsavedChangesChange} />
        </div>
      </section>
    </div>
  );
};

export default ClientExperienceSettings;
